import {
  ApplicationFlag,
  FamilyApplicationResp,
  Student,
} from '@/apis/family_application/requests.ts'
import { ATTESTATION_STATUS } from '@/app/pages/family_application/utils.ts'
import { attestationRoute } from '@/routing/family_application_routes.tsx'
import { FAMILY_APPLICATION_URLS } from '@/routing/route_paths.ts'
import { findMissingRequiredKeys, isEmpty } from './step-checker.utils'
import { ValidatedStep } from './step_checker'

const BC_DOCUMENT_PROP_NAME = 'birth_certificate_document_id' as const

const RequiredStudentData = {
  details: ['first_name', 'last_name', 'relationship'],
  capture: ['date_of_birth', BC_DOCUMENT_PROP_NAME],
}

export function validateSingleStudent(
  student: Student,
  application: FamilyApplicationResp
): ValidatedStep {
  const studentStep: ValidatedStep = {
    status: 'completed',
    urls: [],
    missingFields: [],
  }

  // Validate student information
  const studentInfoValidator = checkMissingStudentFields({
    ...student,
    flags: application.flags,
  })

  if (studentInfoValidator.missingRequiredInfo) {
    studentStep.status = 'not_completed'
    if (student.inception_status == 'Family Renewal Applicant') {
      studentStep.student_id = student.id
    }
    if (studentInfoValidator.missingDetailFields.length > 0) {
      studentStep.urls.push(
        FAMILY_APPLICATION_URLS.student_details.enter_details
      )
      studentStep.missingFields.push(
        ...studentInfoValidator.missingDetailFields
      )
    }
    if (studentInfoValidator.missingBirthDateFields.length > 0) {
      studentStep.urls.push(FAMILY_APPLICATION_URLS.student_details.capture)
      studentStep.missingFields.push(
        ...studentInfoValidator.missingBirthDateFields
      )
    }
    return studentStep
  }

  // Check student attestations
  const familyAttestations = application.attestations
  const firstUnconfirmedAttest = familyAttestations
    .filter((attestation) => attestation.student_id == student.id)
    .sort((a, b) => a.order - b.order)
    .find(
      (attest) =>
        attest.status == ATTESTATION_STATUS.NOT_CONFIRMED &&
        attest.student_id == student.id
    )

  if (firstUnconfirmedAttest) {
    studentStep.status = 'not_completed'
    studentStep.student_id = student.id
    studentStep.urls.push(
      attestationRoute(firstUnconfirmedAttest.id.toString())
    )
  }

  return studentStep
}
export function getEligibleNewStudents(application: FamilyApplicationResp) {
  return application.students.filter(
    (student) =>
      student.application_status !== 'Withdrawn' &&
      (!student.is_external_student ||
        student.inception_status == 'Family Renewal Applicant')
  )
}

function getAllRequiredStudentKeys(): string[] {
  return [...RequiredStudentData.details, ...RequiredStudentData.capture]
}

export function validateStudents(
  application: FamilyApplicationResp
): ValidatedStep {
  const studentStep: ValidatedStep = {
    status: 'completed',
    urls: [],
    missingFields: [],
  }

  const eligibleStudents = getEligibleNewStudents(application)

  if (isEmpty(eligibleStudents)) {
    studentStep.status = 'not_completed'
    studentStep.urls.push(
      FAMILY_APPLICATION_URLS.student_details.additional_student
    )
    return studentStep
  }

  for (const student of eligibleStudents) {
    const studentInfoValidator = checkMissingStudentFields({
      ...student,
      flags: application.flags,
    })

    if (studentInfoValidator.missingRequiredInfo) {
      studentStep.status = 'not_completed'
      if (student.inception_status == 'Family Renewal Applicant') {
        studentStep.student_id = student.id
      }
      if (studentInfoValidator.missingDetailFields.length > 0) {
        studentStep.urls.push(
          FAMILY_APPLICATION_URLS.student_details.enter_details
        )
        studentStep.missingFields.push(
          ...studentInfoValidator.missingDetailFields
        )
      }
      if (studentInfoValidator.missingBirthDateFields.length > 0) {
        studentStep.urls.push(FAMILY_APPLICATION_URLS.student_details.capture)
        studentStep.missingFields.push(
          ...studentInfoValidator.missingBirthDateFields
        )
      }
      break
    }

    // Check student attestations
    const familyAttestations = application.attestations
    const firstUnconfirmedAttest = familyAttestations
      .filter((attestation) => attestation.student_id == student.id)
      .sort((a, b) => a.order - b.order)
      .find(
        (attest) =>
          attest.status == ATTESTATION_STATUS.NOT_CONFIRMED &&
          attest.student_id == student.id
      )

    if (firstUnconfirmedAttest) {
      studentStep.status = 'not_completed'
      studentStep.student_id = student.id
      studentStep.urls.push(
        attestationRoute(firstUnconfirmedAttest.id.toString())
      )
      break
    }
  }

  return studentStep
}

export function hasIncompleteStudentInfo(student: Student): boolean {
  return (
    findMissingRequiredKeys(student, getAllRequiredStudentKeys()).length > 0
  )
}

function checkMissingStudentFields(
  student: Student & { flags: ApplicationFlag[] }
) {
  const missingDetailFields = findMissingRequiredKeys(
    student,
    RequiredStudentData.details
  )
  const approvedAffidavit = student.flags.some(
    (flag) =>
      flag.flag_code == 'student_age_affidavit' && flag.status == 'Resolved'
  )

  const filteredBirthDateFields = RequiredStudentData.capture.filter(
    (field) => field !== BC_DOCUMENT_PROP_NAME
  )

  // When the student has an approved Affidavit flag we don't care about BC on file
  const missingBirthDateFields = findMissingRequiredKeys(
    student,
    approvedAffidavit || student.relationship != 'parent'
      ? filteredBirthDateFields
      : RequiredStudentData.capture
  )

  return {
    missingDetailFields,
    missingBirthDateFields,
    missingRequiredInfo:
      missingDetailFields.length > 0 || missingBirthDateFields.length > 0,
  }
}

function checkMissingStudentAttestations(
  student: Student,
  application?: FamilyApplicationResp
) {
  const missingAttestations = application?.attestations
    .filter((attestation) => attestation.student_id == student.id)
    .sort((a, b) => a.order - b.order)
    .find(
      (attest) =>
        attest.status == ATTESTATION_STATUS.NOT_CONFIRMED &&
        attest.student_id == student.id
    )

  return {
    missingAttestations,
    missingRequiredInfo: missingAttestations != undefined,
  }
}

export function getCompletedStudents(
  students: Student[],
  application?: FamilyApplicationResp
): number[] {
  const buffer: number[] = []
  for (const student of students) {
    const hasIncompleteStudentInfo =
      checkMissingStudentFields({ ...student, flags: application?.flags ?? [] })
        .missingRequiredInfo ||
      checkMissingStudentAttestations(student, application).missingRequiredInfo

    if (!hasIncompleteStudentInfo) {
      buffer.push(student.id)
    }
  }
  return buffer
}

export function getStudentNextStep(
  student: Student,
  application: FamilyApplicationResp
): ValidatedStep {
  const studentInfoValidator = checkMissingStudentFields({
    ...student,
    flags: application.flags,
  })
  const nextStep: ValidatedStep = {
    status: 'completed',
    urls: [],
    missingFields: [],
  }

  if (studentInfoValidator.missingRequiredInfo) {
    nextStep.status = 'not_completed'
    if (studentInfoValidator.missingDetailFields.length > 0) {
      nextStep.urls.push(FAMILY_APPLICATION_URLS.student_details.enter_details)
      nextStep.missingFields.push(...studentInfoValidator.missingDetailFields)
    }
    if (studentInfoValidator.missingBirthDateFields.length > 0) {
      nextStep.urls.push(
        FAMILY_APPLICATION_URLS.student_details.capture_instructions
      )
      nextStep.missingFields.push(
        ...studentInfoValidator.missingBirthDateFields
      )
    }
  }

  const familyAttestations = application.attestations
  const firstUnconfirmedAttest = familyAttestations
    .filter((attestation) => attestation.student_id == student.id)
    .sort((a, b) => a.order - b.order)
    .find(
      (attest) =>
        attest.status == ATTESTATION_STATUS.NOT_CONFIRMED &&
        attest.student_id == student.id
    )

  if (firstUnconfirmedAttest) {
    nextStep.status = 'not_completed'
    nextStep.student_id = student.id
    nextStep.urls.push(attestationRoute(firstUnconfirmedAttest.id.toString()))
  }

  return nextStep
}

export function isRenewalStudentFirstTime(studentStep: ValidatedStep): boolean {
  return studentStep.missingFields.includes('relationship')
}

export function isGeneralStudentFirstTime(studentStep: ValidatedStep): boolean {
  return studentStep.missingFields.includes('first_name')
}

export function renewalStudentHasCompletedApplication(
  application: FamilyApplicationResp,
  student: Student
) {
  // The student is ineligible and cant renew their scholarship.
  // Just mark app as completed so they can't proceed to the renewal flow.
  // Withdrawn added to the conditional so that it has the same effect as Ineligible
  if (
    student.application_status === 'Ineligible' ||
    student.application_status === 'Withdrawn' || 
    student.v1_eligible_for_renewal === false
  ) {
    return true
  }

  const studentAttestations = application.attestations.filter(
    (att) => att.student_id === student.id
  )

  return studentAttestations.every(
    (att) => att.status === ATTESTATION_STATUS.CONFIRMED
  )
}
